import { Link } from "react-router-dom";
import { ShieldCheck, Hammer, Users, Award, CheckCircle2, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { BUSINESS, ASSETS } from "@/data";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity First", desc: "Honest communication and transparent pricing on every project, no surprises." },
  { icon: Hammer, title: "True Craftsmanship", desc: "Every joint, finish and detail is executed with the care of a seasoned builder." },
  { icon: Users, title: "Built On Relationships", desc: "Many of our clients become friends — and refer their families to us for years." },
  { icon: Award, title: "Proven Experience", desc: `Owners with over ${BUSINESS.experienceYears} years of combined building experience.` },
];

const POINTS = [
  "Locally owned and rooted in Reno, NV",
  "Hands-on owner involvement from start to finish",
  "Custom homes, remodels and outdoor living spaces",
  "Owners with 60+ years of combined building experience",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title={<>BUILT ON<br />REPUTATION.</>}
        subtitle={`A homebuilder powered by owners who bring more than ${BUSINESS.experienceYears} years of combined experience to every project.`}
        image={ASSETS.artisan}
      />

      {/* Story */}
      <section className="bg-cream" style={{ backgroundColor: "var(--l3-cream)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div className="overflow-hidden rounded-sm shadow-2xl">
            <img src={ASSETS.artisan} alt="L3 Construction craftsman at work" className="w-full h-[460px] object-cover" data-testid="about-image" />
          </div>
          <div>
            <p className="font-cond tracking-[0.3em] text-sm mb-4" style={{ color: "var(--l3-gold)" }}>OUR STORY</p>
            <h2 className="font-display text-4xl sm:text-5xl mb-6" style={{ color: "var(--l3-wood-dark)" }}>
              EXPERIENCE YOU CAN BUILD ON
            </h2>
            <div className="space-y-4 text-[16px] leading-relaxed" style={{ color: "#5b4f3d" }}>
              <p>
                L3 Construction was founded on a simple belief: a home should be built with the same care you'd
                put into your own. While we're a tight-knit local company, our owners aren't new to the trade —
                together they bring more than {BUSINESS.experienceYears} years of combined homebuilding experience
                to the table.
              </p>
              <p>
                That hands-on knowledge shows up in every custom home, remodel and outdoor space we create across
                {" "}{BUSINESS.serviceArea}. When you work with us, you work directly with seasoned builders who care
                about every nail, every seam and every handshake — and who stand behind their work long after the
                keys are handed over.
              </p>
            </div>
            <ul className="mt-8 space-y-3">
              {POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3" style={{ color: "var(--l3-wood-dark)" }}>
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: "var(--l3-gold)" }} />
                  <span className="text-[15px]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-wood-dark" style={{ backgroundColor: "var(--l3-wood-dark)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="text-center mb-14">
            <p className="font-cond tracking-[0.3em] text-gold text-sm mb-3">WHAT WE STAND FOR</p>
            <h2 className="font-display heading-distress text-4xl sm:text-5xl">OUR CORE VALUES</h2>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <div key={v.title} data-testid={`value-${i}`} className="bg-black/25 border border-white/10 rounded-sm p-8 hover:border-[var(--l3-gold)] transition-colors duration-300">
                <v.icon size={40} strokeWidth={1.5} style={{ color: "var(--l3-gold)" }} />
                <h3 className="font-cond font-semibold tracking-wide text-lg mt-5 mb-2" style={{ color: "var(--l3-cream)" }}>
                  {v.title.toUpperCase()}
                </h3>
                <p className="text-white/60 text-[15px] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}

export function CTABand() {
  return (
    <section className="bg-charcoal" style={{ backgroundColor: "var(--l3-charcoal)" }}>
      <div className="max-w-5xl mx-auto px-6 py-16 text-center">
        <h2 className="font-display heading-distress text-4xl sm:text-5xl mb-4">READY TO BUILD?</h2>
        <p className="text-white/70 max-w-2xl mx-auto text-lg mb-8">
          Let's talk about your project. Reach out for a no-pressure conversation and a free quote.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/contact" data-testid="cta-quote-btn" className="inline-flex items-center gap-2 font-cond tracking-widest px-9 py-4 btn-gold rounded-sm">
            GET A QUOTE <ArrowRight size={18} />
          </Link>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center font-cond tracking-widest px-9 py-4 btn-outline-gold rounded-sm">
            CALL {BUSINESS.phone}
          </a>
        </div>
      </div>
    </section>
  );
}
